/**
 * Codes bancaires conformes à l'API Cofidis
 * Source: Documentation API Cofidis - Codes organismes bancaires
 */

export interface BankOption {
  value: string;
  label: string;
}

/**
 * Mapping des codes courts vers les codes numériques
 * Utilisé pour la transformation des données avant envoi à l'API
 */
export const BANK_CODE_MAPPING: Record<string, string> = {
  'BNP': '30004',
  'BPOP': '13507',
  'CA': '30006',
  'CDN': '30076',
  'CE': '19505',
  'CIC': '30066',
  'CL': '30002',
  'CM': '10278',
  'SG': '30003',
  'MONABQ': '14690'
};

/**
 * Liste complète des banques avec leurs codes numériques
 * Ces codes sont ceux attendus par l'API Cofidis pour le compte bancaire principal
 */
export const BANK_OPTIONS: BankOption[] = [
  { value: '30004', label: 'BNP Paribas' },
  { value: '30003', label: 'Société Générale' },
  { value: '30002', label: 'LCL - Crédit Lyonnais' },
  { value: '30006', label: 'Crédit Agricole' },
  { value: '10278', label: 'Crédit Mutuel' },
  { value: '30066', label: 'CIC' },
  { value: '19505', label: 'Caisse d\'Épargne' },
  { value: '20041', label: 'La Banque Postale' },
  { value: '13507', label: 'Banque Populaire' },
  { value: '30076', label: 'Crédit du Nord' },
  { value: '40618', label: 'Boursorama' },
  { value: '14518', label: 'Fortuneo' },
  { value: '14559', label: 'ING Direct' },
  { value: '14690', label: 'Monabanq' },
  { value: '30588', label: 'Barclays' },
  { value: '12548', label: 'AXA Banque' },
  { value: '26310', label: 'Sygma Banque' },
  { value: '99999', label: 'Autre banque' }
];

/**
 * Liste des organismes prêteurs pour le crédit immobilier
 * Ces codes courts sont attendus par l'API Cofidis pour housingLoanOrganizationCode
 */
export const HOUSING_LOAN_BANK_OPTIONS: BankOption[] = [
  { value: 'ACCORD', label: 'Oney' },
  { value: 'AUTBQE', label: 'Autre banque' },
  { value: 'BNP', label: 'BNP Paribas' },
  { value: 'BPOP', label: 'Banque Populaire' },
  { value: 'CA', label: 'Crédit Agricole' },
  { value: 'CDN', label: 'Crédit du Nord' },
  { value: 'CE', label: 'Caisse d\'Épargne' },
  { value: 'CETELE', label: 'Cetelem' },
  { value: 'CFF', label: 'Crédit Foncier de France' },
  { value: 'CIC', label: 'CIC (Créd. Indus. et Cial)' },
  { value: 'CL', label: 'LCL (Crédit Lyonnais)' },
  { value: 'CM', label: 'Crédit Mutuel' },
  { value: 'COFINO', label: 'Cofinoga (Mediatis)' },
  { value: 'CREDIP', label: 'Credipar (PSA)' },
  { value: 'DIAC', label: 'Diac' },
  { value: 'FACET', label: 'Facet' },
  { value: 'FIDEM', label: 'Fidem' },
  { value: 'FINANC', label: 'Financo' },
  { value: 'FRANFI', label: 'Franfinance' },
  { value: 'GECAPI', label: 'My Money Bank' },
  { value: 'MONABQ', label: 'Monabanq' },
  { value: 'PASS', label: 'Carrefour Banque (Pass)' },
  { value: 'SG', label: 'Société Générale' },
  { value: 'SOCRAM', label: 'Socram Banque' },
  { value: 'SOFINC', label: 'Sofinco' },
  { value: 'VIAXEL', label: 'Viaxel' }
];

/**
 * Codes bancaires par nom complet (pour recherche inverse)
 */
export const BANKS_BY_NAME: Record<string, string> = {
  'AUTRE BANQUE': 'AUTBQE',
  'BNP PARIBAS': 'BNP',
  'BANQUE POPULAIRE': 'BPOP',
  'CREDIT AGRICOLE': 'CA',
  'CREDIT DU NORD': 'CDN',
  'CAISSE D EPARGNE': 'CE',
  'CIC (CRED INDUS. ET CIAL)': 'CIC',
  'LCL (CREDIT LYONNAIS)': 'CL',
  'CREDIT MUTUEL': 'CM',
  'SOCIETE GENERALE': 'SG',
  'MONABANQ': 'MONABQ'
};

/**
 * Codes courts des banques (pour certains traitements internes)
 */
export const SHORT_BANK_CODES: Record<string, string> = {
  'ACCORD': 'ONEY',
  'AUTBQE': 'AUTRE BANQUE',
  'BNP': 'BNP PARIBAS',
  'BPOP': 'BANQUE POPULAIRE',
  'CA': 'CREDIT AGRICOLE',
  'CDN': 'CREDIT DU NORD',
  'CE': 'CAISSE D EPARGNE',
  'CETELE': 'CETELEM',
  'CFF': 'CREDIT FONCIER DE FRANCE',
  'CIC': 'CIC (CRED INDUS. ET CIAL)',
  'CL': 'LCL (CREDIT LYONNAIS)',
  'CM': 'CREDIT MUTUEL',
  'COFINO': 'COFINOGA (MEDIATIS)',
  'CREDIP': 'CREDIPAR (PSA)',
  'DIAC': 'DIAC',
  'FACET': 'FACET',
  'FIDEM': 'FIDEM',
  'FINANC': 'FINANCO',
  'FRANFI': 'FRANFINANCE',
  'GECAPI': 'MY MONEY BANK',
  'MONABQ': 'MONABANQ',
  'PASS': 'CARREFOUR BANQUE (PASS)',
  'SG': 'SOCIETE GENERALE',
  'SOCRAM': 'SOCRAM BANQUE',
  'SOFINC': 'SOFINCO',
  'VIAXEL': 'VIAXEL'
};

/**
 * Tous les codes bancaires numériques avec leurs noms complets
 */
export const NUMERIC_BANK_CODES: Record<string, string> = {
  '10011': 'CNE',
  '10096': 'LYONNAISE DE BANQUE LB',
  '10278': 'CREDIT MUTUEL',
  '12240': 'ALLIANZ',
  '12548': 'AXA BANQUE',
  '12619': 'CAIXA GERAL DE DEPOSITOS',
  '13507': 'BANQUE POPULAIRE',
  '14518': 'FORTUNEO',
  '14559': 'ING DIRECT',
  '14690': 'MONABANQ',
  '15208': 'CREDIT MUNICIPAL',
  '16598': 'NICKEL - Financières des paiements électroniques',
  '19505': 'CAISSE EPARGNE',
  '20041': 'LA BANQUE POSTALE',
  '26310': 'SYGMA BANQUE',
  '30002': 'LCL - CREDIT LYONNAIS',
  '30003': 'SOCIETE GENERALE',
  '30004': 'BNP PARIBAS- BANQUE NATIONALE DE PARIS',
  '30006': 'CREDIT AGRICOLE',
  '30066': 'CIC',
  '30076': 'CREDIT DU NORD',
  '30077': 'STE MARSEILLAISE DE CREDIT',
  '30588': 'BARCLAYS',
  '40031': 'CAISSE DES DEPOTS ET DES CONSIGNATIONS',
  '40618': 'BOURSORAMA',
  '99999': 'Other...'
};

/**
 * Fonction utilitaire pour obtenir le code numérique à partir d'un code court
 */
export function getNumericBankCode(shortCode: string): string | undefined {
  return BANK_CODE_MAPPING[shortCode];
}

/**
 * Fonction utilitaire pour obtenir le nom de la banque à partir du code numérique
 */
export function getBankNameByNumericCode(numericCode: string): string | undefined {
  return NUMERIC_BANK_CODES[numericCode];
}
